package components.project

import components.exportHeading
import components.form.commentSection
import components.project.ServerProjectPreviewsTable.TableColumnId
import components.project.ServerProjectPreviewsTable.getCustomerColumn
import components.project.ServerProjectPreviewsTable.getEditorColumn
import components.project.ServerProjectPreviewsTable.getRelevantProcessStateColumn
import components.project.ServerProjectPreviewsTable.projectDetails
import it.neckar.commons.kotlin.js.LocalStorageKeyPrefix
import it.neckar.lizergy.model.company.CompanyResolver
import it.neckar.lizergy.model.project.previews.ProjectQueryComponent
import it.neckar.lizergy.model.project.previews.ProjectQueryForUser
import it.neckar.lizergy.model.project.previews.ServerProjectPreview
import it.neckar.lizergy.model.project.process.state.DocumentationProcessStateEntry.DocumentationProcessStates
import it.neckar.lizergy.model.project.process.state.FinalAccountProcessStateEntry.FinalAccountProcessStates
import it.neckar.lizergy.model.project.process.state.ProjectProcessStateEntry.ProjectProcessStates
import it.neckar.react.common.form.*
import it.neckar.react.common.table.*
import react.*
import react.dom.*
import router.useDocumentTitle
import services.UiActions
import store.hooks.useRequireCompanyForLoggedInUser
import store.hooks.useSelectCompanyResolver
import store.hooks.useSelectUserResolver

val FinalAccountsOverviewFromUrl: FC<Props> = fc("FinalAccountsOverviewFromUrl") {
  val companyName = useRequireCompanyForLoggedInUser().simpleName
  val userResolver = useSelectUserResolver()
  val companyResolver = useSelectCompanyResolver()
  val relevantProcessStates = FinalAccountProcessStates.entries

  useDocumentTitle(companyName, "Abschlussrechnungen")

  h1("mb-3") { +"Abschlussrechnungen" }

  ProjectsOverview {
    attrs {
      this.processStatesToFilter = relevantProcessStates
      this.processStatesToHide = listOf(FinalAccountProcessStates.Paid, ProjectProcessStates.Archived, ProjectProcessStates.Paused)
      this.projectQueryComponent = ProjectQueryComponent.FinalAccount
      this.projectQueryForUser = ProjectQueryForUser.AssignedEditor
      this.sortedBy = SortedBy.Unsorted
      this.tableColumns = {
        buildList {
          add(getProjectColumnWithFinalAccountComments(companyResolver))
          add(getCustomerColumn())
          add(getEditorColumn(projectQueryComponent, userResolver))
          add(getRelevantProcessStateColumn(projectQueryComponent))
        }
      }
      this.includeArchived = false
      this.keyPrefix = LocalStorageKeyPrefix("FinalAccounts")
    }
  }
}

fun <T : ServerProjectPreview> getProjectColumnWithFinalAccountComments(companyResolver: CompanyResolver): TableDataColumn<T> {
  return TableDataColumn(
    id = TableColumnId.Project.id,
    title = "Projekt",
    sortFunction = compareBy { it.displayName },
  ) { projectPreview ->
    {
      projectDetails(projectPreview, companyResolver)
      div("my-3") {
        commentSection(projectPreview.finalAccountId, editableStatus = EditableStatus.ReadOnly)
      }
    }
  }
}

val DocumentationsOverviewFromUrl: FC<Props> = fc("DocumentationsOverviewFromUrl") {
  val companyName = useRequireCompanyForLoggedInUser().simpleName
  val userResolver = useSelectUserResolver()
  val companyResolver = useSelectCompanyResolver()
  val relevantProcessStates = DocumentationProcessStates.entries

  useDocumentTitle(companyName, "Dokumentationen")

  h1("mb-3") { +"Dokumentationen" }

  ProjectsOverview {
    attrs {
      this.processStatesToFilter = relevantProcessStates
      this.processStatesToHide = listOf(DocumentationProcessStates.Done, ProjectProcessStates.Archived, ProjectProcessStates.Paused)
      this.projectQueryComponent = ProjectQueryComponent.Documentation
      this.tableColumns = { ServerProjectPreviewsTable.defaultColumns(projectQueryComponent = projectQueryComponent, userResolver = userResolver, companyResolver = companyResolver) }
      this.sortedBy = SortedBy.Unsorted
      this.projectQueryForUser = ProjectQueryForUser.AssignedEditor
      this.includeArchived = false
      this.keyPrefix = LocalStorageKeyPrefix("Documentations")
    }
  }
}

val VerificationsOverviewFromUrl: FC<Props> = fc("VerificationsOverviewFromUrl") {
  val companyName = useRequireCompanyForLoggedInUser().simpleName
  val userResolver = useSelectUserResolver()
  val companyResolver = useSelectCompanyResolver()
  val relevantProcessStates = listOf(DocumentationProcessStates.Done, ProjectProcessStates.Archived)

  useDocumentTitle(companyName, "Überprüfung")

  h1("mb-3") { +"Überprüfung" }

  exportHeading("Projekte", buttonLabel = "Alle Projekte als Excel-Export") {
    UiActions.downloadProjectPreviewsXlsx()
  }

  ProjectsOverview {
    attrs {
      this.processStatesToFilter = relevantProcessStates
      this.processStatesToHide = emptyList()
      this.projectQueryComponent = ProjectQueryComponent.Verification
      this.tableColumns = { ServerProjectPreviewsTable.defaultColumns(projectQueryComponent = projectQueryComponent, userResolver = userResolver, companyResolver = companyResolver) }
      this.sortedBy = SortedBy.SortedAscending
      this.projectQueryForUser = ProjectQueryForUser.Any
      this.includeArchived = false
      this.keyPrefix = LocalStorageKeyPrefix("Verifications")
    }
  }
}
